import { ReactNode, useCallback, useState } from "react";

interface CardPhotoProps {
  photoSrc: string;
  /** Brand color for the gradient fallback + selected halo border. */
  primary: string;
  /** Glow color used on the selected box-shadow halo. */
  glow: string;
  isSelected: boolean;
  onClick?: () => void;
  /** Stagger delay (seconds) for the card's intro animation. */
  delay?: number;
  /** Right-side content — label, blurb, "Tap to continue", etc. */
  children: ReactNode;
}

/**
 * Shared picker card used by the stepper's intro screens (CommunityIntro,
 * AgeRangeIntro). Single source of truth for the look-and-feel: 40%-width
 * photo on the left filling the full card height, content on the right, glass
 * surface with a neon halo on selection. Renders a `<button>` so the entire
 * card is the click target.
 *
 * The photo container has no fixed aspect ratio — it stretches to match the
 * tallest flex sibling (the content column), so the image always fills the
 * card edge-to-edge no matter how long the blurb is.
 */
export default function CardPhoto({
  photoSrc,
  primary,
  glow,
  isSelected,
  onClick,
  delay = 0,
  children,
}: CardPhotoProps) {
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);

  /**
   * A cached image is already decoded by the time React attaches to it, and
   * `onLoad` has been and gone: the card was left holding its placeholder
   * gradient until something else re-rendered it, then cut to the photo. That
   * is the blink — the fade is 200ms and the cut is instant.
   */
  const imgRef = useCallback((node: HTMLImageElement | null) => {
    if (node?.complete && node.naturalWidth > 0) setLoaded(true);
  }, []);

  return (
    <button
      onClick={onClick}
      // The entrance is a CSS keyframe. It was a framer `initial/animate` on
      // opacity while this same element carried `transition-all`, so the CSS
      // transition was chasing every frame framer wrote — two engines easing
      // the same property at once, which on Safari reads as a blink rather
      // than a fade.
      //
      // `transition-all` is now the properties that actually change: the
      // border and halo on selection, and the scale on press. Nothing else
      // here is animated, and naming them keeps anything added later from
      // being animated by accident.
      style={{
        animationDelay: `${delay}s`,
        ...(isSelected
          ? {
              border: `1px solid ${primary}`,
              boxShadow: `0 0 0 4px ${glow}, 0 8px 30px ${glow}`,
            }
          : {}),
      }}
      className={`animate-fade-in relative overflow-hidden text-left transition-[border-color,box-shadow,transform] duration-200 flex rounded-2xl card flex-row p-0 ${onClick && "active:scale-[0.99]"}`}
    >
      <div className="relative w-[40%] flex-shrink-0 self-stretch min-h-[140px]">
        {!errored && (
          <img
            ref={imgRef}
            src={photoSrc}
            alt=""
            // Not lazy: all of these are on screen the moment the card is, so
            // deferring the fetch only guarantees the placeholder is what the
            // user sees first.
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={() => setErrored(true)}
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-200"
            style={{ opacity: loaded ? 1 : 0 }}
          />
        )}
        {(!loaded || errored) && (
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(135deg, ${primary} 0%, #1A0E26 130%)`,
            }}
          />
        )}
      </div>

      {/* Content column */}
      <div className="flex-1 px-4 py-4 flex flex-col justify-center">
        {children}
      </div>
    </button>
  );
}
